/**
 * Payout account hooks (React Query)
 *
 * The signed-in professional's (designer/contractor) payout account. Read is
 * gated behind `enabled` so it only fires for professionals; the save mutation
 * routes to create-or-update and invalidates the cached account on success.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createPayoutAccount,
	fetchMyPayoutAccount,
	updatePayoutAccount,
} from "@/features/projects/services/projects-api.service";
import { queryKeys } from "@/lib/query-keys";

export interface PayoutAccountInput {
	accountHolderName: string;
	accountNumber: string;
	ifscCode: string;
}

export function usePayoutAccount(enabled: boolean) {
	return useQuery({
		queryKey: queryKeys.payouts.me(),
		queryFn: fetchMyPayoutAccount,
		enabled,
	});
}

export function useSavePayoutAccount() {
	const queryClient = useQueryClient();
	return useMutation({
		// An existing `id` means update; otherwise create.
		mutationFn: (input: { id?: string; data: PayoutAccountInput }) =>
			input.id
				? updatePayoutAccount(input.id, input.data)
				: createPayoutAccount(input.data),
		onSuccess: (result) => {
			if (result.success) {
				queryClient.invalidateQueries({ queryKey: queryKeys.payouts.me() });
			}
		},
	});
}
